import { type KeyboardEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { trackEvent } from "@lib/infrastructure";
import { HaNViewStoreContext } from "@lib/logic";
import { getAllBuildings, getDefaultBuilding, useAppSelector } from "@lib/store";
import { useRequiredContext } from "@lib/utils";
import { Icon } from "@atoms";

import { useConfirmDiscardingBooking } from "../../../details/useConfirmDiscardingBooking";
import { HaNContext } from "../../HaNContext";
import { BuildingOption } from "./BuildingOption";

/** Up and down move the focus through the buildings' buttons, one per row of the list, starting from the search. */
const moveFocus = (event: KeyboardEvent<HTMLDivElement>) => {
  if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;

  const options = [...event.currentTarget.querySelectorAll<HTMLButtonElement>("li > button")];

  if (options.length === 0) return;

  event.preventDefault();

  const index = options.findIndex(option => option === document.activeElement);
  const step = event.key === "ArrowDown" ? 1 : -1;

  if (index === -1) {
    options.at(step === 1 ? 0 : -1)?.focus();
    return;
  }

  options[(index + step + options.length) % options.length]?.focus();
};

/**
 * Scrolls the list so the shown building is in its middle. It only runs when that building's row mounts, so the list
 * opens on it and stays put while the user types. It sets the list's own scroll, so nothing around it moves.
 */
const centreInList = (item: HTMLLIElement | null) => {
  const list = item?.parentElement;

  if (!item || !list) return;

  list.scrollTop = item.offsetTop - (list.clientHeight - item.offsetHeight) / 2;
};

/**
 * The buildings to choose from, the default one first, narrowed down by name as the user types. Enter in the search
 * chooses the first match. Choosing asks first when that would throw away a booking with changes.
 */
export const BuildingList = ({ onChoose }: { onChoose: () => void }) => {
  const { t } = useTranslation();
  const { building } = useRequiredContext(HaNContext);
  const viewStore = useRequiredContext(HaNViewStoreContext);
  const buildings = useAppSelector(getAllBuildings);
  const defaultBuildingId = useAppSelector(getDefaultBuilding)?.id;
  const confirmDiscardingBooking = useConfirmDiscardingBooking();
  const [search, setSearch] = useState("");

  const query = search.trim().toLowerCase();
  const shownBuildings = buildings
    .toSorted((a, b) => Number(b.id === defaultBuildingId) - Number(a.id === defaultBuildingId))
    .filter(b => b.name.toLowerCase().includes(query));

  const choose = (buildingId: string) => {
    onChoose();

    if (buildingId === building.buildingId) return;

    confirmDiscardingBooking(() => {
      trackEvent("HereAndNow__ChangeBuilding", { id: buildingId });

      building.selectBuilding(buildingId);
      viewStore.deselectAll();
    });
  };

  return (
    <div
      className="flex min-h-0 flex-col"
      onKeyDown={moveFocus}>
      <div className="relative flex-none border-b p-3">
        <Icon
          aria-hidden
          icon="search"
          color="currentColor"
          className="pointer-events-none absolute left-6 top-1/2 size-4 -translate-y-1/2 text-mapiq-black-400"
        />
        <input
          type="search"
          aria-label={t("hereAndNow:SearchBuildings")}
          placeholder={t("hereAndNow:SearchBuildings")}
          value={search}
          className="w-full rounded-lg bg-mapiq-black-50 py-2 pl-9 pr-3 placeholder:text-mapiq-black-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mapiq-black-800*"
          onChange={event => setSearch(event.target.value)}
          onKeyDown={event => {
            const firstMatch = shownBuildings[0];

            if (event.key !== "Enter" || !firstMatch) return;

            event.preventDefault();
            choose(firstMatch.id);
          }}
        />
      </div>

      {shownBuildings.length > 0 ? (
        <ul className="relative flex min-h-0 flex-col gap-1 overflow-y-auto p-2">
          {shownBuildings.map(b => (
            <li
              key={b.id}
              ref={b.id === building.buildingId ? centreInList : undefined}>
              <BuildingOption
                name={b.name}
                isSelected={b.id === building.buildingId}
                isDefault={b.id === defaultBuildingId}
                onChoose={() => choose(b.id)}
              />
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-4 py-6 text-center text-mapiq-black-500">{t("hereAndNow:NoBuildingsFound")}</p>
      )}
    </div>
  );
};
